import React, { useState, useEffect } from 'react';
import styles from './ExceptionGraphs.module.css';
import TopNRecordsLineGraph from '../TopNRecordsLineGraph/TopNRecordsLineGraph';
import {
  getDayWiseEmergencyAsnReasonsGraphData,
  getDayWiseEmergencyAsnAgenciesGraphData,
  getWeekWiseEmergencyAsnReasonsGraphData,
  getWeekWiseEmergencyAsnAgenciesGraphData,
  getDayWiseArchivedAsnReasonsGraphData,
  getDayWiseArchivedAsnAgenciesGraphData,
  getWeekWiseArchivedAsnReasonsGraphData,
  getWeekWiseArchivedAsnAgenciesGraphData,
} from '../../utils/graphDataUtils';
import { API_RESOURCE_URLS, TIME_DURATION } from '../../constants';
import Radio from '@material-ui/core/Radio';
import { EXCEPTION_GRAPH_TYPE } from '../../staticData';
import { API } from '../../apis/api';
import axios from 'axios';

const ExceptionGraphs = ({
  plant,
  line,
  durationText,
  startDateTime,
  endDateTime,
  selectedGraph,
  handleGraphSelection,
  ...props
}) => {
  const [
    emergencyAsnReasonsGraphData,
    setEmergencyAsnReasonsGraphData,
  ] = useState([]);
  const [
    emergencyAsnAgenciesGraphData,
    setEmergencyAsnAgenciesGraphData,
  ] = useState([]);
  const [reasonMaxCount, setReasonMaxCount] = useState(0);
  const [agencyMaxCount, setAgencyMaxCount] = useState(0);

  const [
    archivedAsnReasonsGraphData,
    setArchivedAsnReasonsGraphData,
  ] = useState([]);
  const [
    archivedAsnAgenciesGraphData,
    setArchivedAsnAgenciesGraphData,
  ] = useState([]);
  const [archivedAsnReasonMaxCount, setArchivedAsnReasonMaxCount] = useState(0);
  const [archivedAsnAgencyMaxCount, setArchivedAsnAgencyMaxCount] = useState(0);

  const [rawDataForEmergencyAsn, setRawDataForEmergencyAsn] = useState([]);
  const [rawDataForArchivedAsn, setRawDataForArchivedAsn] = useState([]);
  const [isEmergencyAsnGraphLoading, setIsEmergencyAsnGraphLoading] = useState(
    true,
  );
  const [isArchivedAsnGraphLoading, setIsArchivedAsnGraphLoading] = useState(
    true,
  );

  const fetchEmergencyAsnStatisticsDataForGivenDateRange = async (
    selectedPlant,
    selectedLine,
    selectedStartDateTime,
    selectedEndDateTime,
    APICancelTokenSource,
  ) => {
    const response = await API.get(API_RESOURCE_URLS.EMERGENCY_ASN_STATISTICS, {
      params: {
        plant: selectedPlant,
        line: selectedLine,
        from: selectedStartDateTime,
        to: selectedEndDateTime,
      },
      cancelToken: APICancelTokenSource.token,
    });
    return response.data;
  };

  const fetchArchivedAsnStatisticsDataForGivenDateRange = async (
    selectedPlant,
    selectedLine,
    selectedStartDateTime,
    selectedEndDateTime,
    APICancelTokenSource,
  ) => {
    const response = await API.get(API_RESOURCE_URLS.ARCHIVED_ASN_STATISTICS, {
      params: {
        plant: selectedPlant,
        line: selectedLine,
        from: selectedStartDateTime,
        to: selectedEndDateTime,
      },
      cancelToken: APICancelTokenSource.token,
    });
    return response.data;
  };

  useEffect(() => {
    setIsEmergencyAsnGraphLoading(true);
    const cancelTokenSource = axios.CancelToken.source();
    fetchEmergencyAsnStatisticsDataForGivenDateRange(
      plant,
      line,
      startDateTime,
      endDateTime,
      cancelTokenSource,
    ).then(res => setRawDataForEmergencyAsn(res));
  }, [plant, line, startDateTime, endDateTime]);

  useEffect(() => {
    setIsArchivedAsnGraphLoading(true);
    const cancelTokenSource = axios.CancelToken.source();
    fetchArchivedAsnStatisticsDataForGivenDateRange(
      plant,
      line,
      startDateTime,
      endDateTime,
      cancelTokenSource,
    ).then(res => setRawDataForArchivedAsn(res));
  }, [plant, line, startDateTime, endDateTime]);

  useEffect(() => {
    const [reasonMaxRecordCount, reasonGraphDataArray] =
      durationText === TIME_DURATION.LAST_SEVEN_DAYS
        ? getDayWiseEmergencyAsnReasonsGraphData(
            rawDataForEmergencyAsn,
            startDateTime,
            endDateTime,
          )
        : getWeekWiseEmergencyAsnReasonsGraphData(
            rawDataForEmergencyAsn,
            startDateTime,
            endDateTime,
          );
    setEmergencyAsnReasonsGraphData(reasonGraphDataArray);
    setReasonMaxCount(reasonMaxRecordCount);

    const [agencyMaxRecordCount, agencyGraphDataArray] =
      durationText === TIME_DURATION.LAST_SEVEN_DAYS
        ? getDayWiseEmergencyAsnAgenciesGraphData(
            rawDataForEmergencyAsn,
            startDateTime,
            endDateTime,
          )
        : getWeekWiseEmergencyAsnAgenciesGraphData(
            rawDataForEmergencyAsn,
            startDateTime,
            endDateTime,
          );
    setEmergencyAsnAgenciesGraphData(agencyGraphDataArray);
    setIsEmergencyAsnGraphLoading(false);
    setAgencyMaxCount(agencyMaxRecordCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDataForEmergencyAsn]);

  useEffect(() => {
    const [
      reasonMaxRecordCountForArchivedAsn,
      reasonGraphDataArrayForArchivedAsn,
    ] =
      durationText === TIME_DURATION.LAST_SEVEN_DAYS
        ? getDayWiseArchivedAsnReasonsGraphData(
            rawDataForArchivedAsn,
            startDateTime,
            endDateTime,
          )
        : getWeekWiseArchivedAsnReasonsGraphData(
            rawDataForArchivedAsn,
            startDateTime,
            endDateTime,
          );
    setArchivedAsnReasonsGraphData(reasonGraphDataArrayForArchivedAsn);
    setArchivedAsnReasonMaxCount(reasonMaxRecordCountForArchivedAsn);

    const [
      agencyMaxRecordCountForArchivedAsn,
      agencyGraphDataArrayForArchivedAsn,
    ] =
      durationText === TIME_DURATION.LAST_SEVEN_DAYS
        ? getDayWiseArchivedAsnAgenciesGraphData(
            rawDataForArchivedAsn,
            startDateTime,
            endDateTime,
          )
        : getWeekWiseArchivedAsnAgenciesGraphData(
            rawDataForArchivedAsn,
            startDateTime,
            endDateTime,
          );
    setArchivedAsnAgenciesGraphData(agencyGraphDataArrayForArchivedAsn);
    setIsArchivedAsnGraphLoading(false);
    setArchivedAsnAgencyMaxCount(agencyMaxRecordCountForArchivedAsn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDataForArchivedAsn]);

  const handleChange = event => {
    handleGraphSelection(event.target.value);
  };
  return (
    <>
      <div>
        <div className={styles.innerGraphContainer}>
          <div
            className={
              selectedGraph !== EXCEPTION_GRAPH_TYPE.EMERGENCY_ASN &&
              styles.inActiveGraph
            }
          >
            <div className={styles.topNRecordsLineGraph}>
              <div className={styles.heading}>
                <Radio
                  checked={selectedGraph === EXCEPTION_GRAPH_TYPE.EMERGENCY_ASN}
                  value={EXCEPTION_GRAPH_TYPE.EMERGENCY_ASN}
                  onChange={handleChange}
                  color="default"
                  className={styles.graphRadio}
                />
                Emergency ASN - Reason (Top contributors)
              </div>
              <TopNRecordsLineGraph
                data={emergencyAsnReasonsGraphData}
                maxRecordCount={reasonMaxCount}
                isLoading={isEmergencyAsnGraphLoading}
                durationText={durationText}
                label="count"
              />
            </div>

            <div className={styles.topNRecordsLineGraph}>
              <div className={styles.heading}>
                Emergency ASN - Agency (Top contributors)
              </div>
              <TopNRecordsLineGraph
                data={emergencyAsnAgenciesGraphData}
                maxRecordCount={agencyMaxCount}
                isLoading={isEmergencyAsnGraphLoading}
                durationText={durationText}
                label="count"
              />
            </div>
          </div>

          <div
            className={
              selectedGraph !== EXCEPTION_GRAPH_TYPE.ARCHIVED_ASN &&
              styles.inActiveGraph
            }
          >
            <div className={styles.topNRecordsLineGraph}>
              <div className={styles.heading}>
                <Radio
                  checked={selectedGraph === EXCEPTION_GRAPH_TYPE.ARCHIVED_ASN}
                  value={EXCEPTION_GRAPH_TYPE.ARCHIVED_ASN}
                  onChange={handleChange}
                  color="default"
                  className={styles.graphRadio}
                />
                Archived ASN - Reason (Top contributors)
              </div>
              <TopNRecordsLineGraph
                data={archivedAsnReasonsGraphData}
                maxRecordCount={archivedAsnReasonMaxCount}
                isLoading={isArchivedAsnGraphLoading}
                durationText={durationText}
                label="count"
              />
            </div>
            <div className={styles.topNRecordsLineGraph}>
              <div className={styles.heading}>
                Archived ASN - Agency (Top contributors)
              </div>
              <TopNRecordsLineGraph
                data={archivedAsnAgenciesGraphData}
                maxRecordCount={archivedAsnAgencyMaxCount}
                isLoading={isArchivedAsnGraphLoading}
                durationText={durationText}
                label="count"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExceptionGraphs;
